import React, { useEffect, useRef, useState } from "react";

import { animated, useSpring } from "@react-spring/web";
import ArrowLeftIcon from "@uui/ArrowLeft";
import emailValidator from "email-validator";
import { find, findIndex, isEmpty, map, remove } from "lodash/fp";

import { Collapse, FormControlLabel, Typography } from "@mui/material";

import useTopicReportImpersonations from "@app/analytics/TopicReport/hooks/useTopicReportImpersonations";
import type {
  Topic,
  TopicImpersonationCreateEntity,
} from "@app/analytics/types";
import * as Events from "@app/common/access/ShareReport/events";
import useEditSharing from "@app/common/access/ShareReport/hooks/useEditSharing";
import useWorkspaceSearch from "@app/common/access/ShareReport/hooks/useWorkspaceSearch";
import {
  AccessListCollapse,
  AccessListWrapper,
  Dialog,
  Container,
  ContentContainer,
  CloseButton,
  Header,
  Footer,
  SharingButton,
} from "@app/common/access/ShareReport/styles";
import { toast } from "@app/common/hooks/useToasts";
import type { ShareEntity } from "@app/common/types";
import useWorkspace from "@app/common/workspaces/useWorkspace";
import { replaceAt } from "@app/utils/arrays";

import AddShareEntitiesInput from "@rui/AddShareEntitiesInput";
import Checkbox from "@rui/Checkbox";
import IconButton from "@rui/IconButton";
import ModalTitle from "@rui/Modal/ModalTitle";
import { Column, Row } from "@rui/Stack";
import TextField from "@rui/TextField";

import ShareTopicReportAccessList from "./ShareTopicReportAccessList";

const AnimatedRow = animated(Row);

const validateShareCandidates = (entities: ShareEntity[]) => {
  const candidates = remove<ShareEntity>(
    (entity) => typeof entity === "string" && !emailValidator.validate(entity),
    entities
  );

  return candidates.length === entities.length;
};

interface ShareTopicReportDialogProps {
  onClose: () => void;
  open: boolean;
  topic: Topic;
  initEntityEmail: string | null;
}

const ShareTopicReportDialog = ({
  onClose,
  open,
  topic,
  initEntityEmail,
}: ShareTopicReportDialogProps) => {
  const notifyMessageRef = useRef<HTMLTextAreaElement | null>(null);
  const [notifyViaEmail, setNotifyViaEmail] = useState(true);

  const { workspaceId } = useWorkspace();
  const {
    impersonations,
    createImpersonations,
    deleteImpersonation,
    isError,
    isLoading,
  } = useTopicReportImpersonations();

  const { searchResults } = useWorkspaceSearch(initEntityEmail, workspaceId, {
    enabled: initEntityEmail !== null,
  });

  const {
    clearSelectedEntities,
    isAddingEntities,
    onChangeSelectedEntities,
    selectedEntities,
  } = useEditSharing();

  // This effect handles managing an initially-selected email address
  // to share with and updating the share entity after receiving more
  // data from the workspace search result
  useEffect(() => {
    if (
      !open ||
      initEntityEmail === null ||
      (selectedEntities.includes(initEntityEmail) && isEmpty(searchResults))
    ) {
      return;
    }

    onChangeSelectedEntities((prevState) => {
      const existingIndex = findIndex(
        (email) => email === initEntityEmail,
        map(
          (entity) => (typeof entity === "string" ? entity : entity.email),
          prevState
        )
      );

      const newEntity = searchResults[0] ?? initEntityEmail;

      if (existingIndex !== -1) {
        return replaceAt(prevState, newEntity, existingIndex);
      }

      return [...prevState, newEntity];
    });
  }, [searchResults, initEntityEmail, open]);

  useEffect(() => {
    if (open) return;
    clearSelectedEntities();
  }, [open]);

  const handleRemoveAccess = (entityId: string) => {
    const entity = find({ entityId }, impersonations);
    if (!entity) return;

    deleteImpersonation.mutate(
      {
        entityId,
        entityKind: entity.kind,
        topicId: topic.id,
      },
      {
        onError: () => {
          toast.error(
            `Something went wrong and your changes could not be saved. Please try again or contact support@read.ai if the problem persists.`
          );
        },
      }
    );
  };

  const handleShare = () => {
    const allEntitiesAreValid = validateShareCandidates(selectedEntities);

    if (!allEntitiesAreValid) {
      toast.error("Please enter valid email addresses");
      return;
    }

    const notifyMessage = notifyViaEmail
      ? notifyMessageRef.current?.value
      : undefined;

    const entities: TopicImpersonationCreateEntity[] = selectedEntities.map(
      (entity) => {
        const isEmailEntity = typeof entity === "string";
        return {
          entityId: isEmailEntity ? entity : entity.id,
          kind: isEmailEntity ? "email" : "user",
          message: notifyMessage,
          notifyUser: notifyViaEmail,
        };
      }
    );

    createImpersonations.mutate(
      { topicId: topic.id, entities },
      {
        onSuccess: () => {
          Events.shareReportSuccess({
            type: "topic",
            channel_provider: topic.provider,
          });
          toast.success("Changes saved!");
          clearSelectedEntities();
        },
        onError: () => {
          toast.error(
            `Something went wrong and your changes could not be saved. Please try again or contact support@read.ai if the problem persists.`
          );
        },
      }
    );
  };

  const backArrowProps = useSpring({
    opacity: isAddingEntities ? 1 : 0,
    width: isAddingEntities ? "44px" : "0px",
    delay: isAddingEntities ? 0 : 400,
    marginRight: isAddingEntities ? 8 : 0,
    config: { tension: 500 },
  });

  const modalTitle = `Share Report: ${topic.title}`;

  return (
    <Dialog open={open} onClose={onClose}>
      <Container spacing={1.5}>
        <Header>
          <Row variant="apart">
            <AnimatedRow style={backArrowProps} variant="left">
              <IconButton onClick={clearSelectedEntities}>
                <ArrowLeftIcon sx={{ fontSize: "20px" }} />
              </IconButton>
            </AnimatedRow>
            <ModalTitle title={modalTitle}>{modalTitle}</ModalTitle>
          </Row>
          <CloseButton onClick={onClose} />
        </Header>
        <ContentContainer spacing={1.5}>
          <AddShareEntitiesInput
            emailOptionPrefix="Invite: "
            onChange={onChangeSelectedEntities}
            selectedEntities={selectedEntities}
            workspaceSearchParams={{ includeEntities: ["user"], workspaceId }}
          />
          <Collapse in={isAddingEntities} unmountOnExit>
            <Column spacing={1}>
              <FormControlLabel
                control={<Checkbox sx={{ mr: 0.5 }} />}
                checked={notifyViaEmail}
                onChange={(e, checked) => setNotifyViaEmail(checked)}
                label={
                  <Typography
                    variant="bodySecondaryReg"
                    color="shades.neutral.700"
                  >
                    Notify via email
                  </Typography>
                }
                sx={{ mx: 0.5 }}
              />
              <Collapse in={notifyViaEmail} unmountOnExit>
                <TextField
                  placeholder="Add a message (optional)"
                  multiline
                  sx={{ width: "100%", p: 0, minWidth: 0 }}
                  ref={notifyMessageRef}
                  InputProps={{ sx: { p: 0 } }}
                />
              </Collapse>
            </Column>
          </Collapse>
          <AccessListCollapse in={!isAddingEntities}>
            <AccessListWrapper spacing={2}>
              <Column spacing={1} flexGrow={{ xxs: 1, sm: undefined }}>
                <Row variant="left" spacing={1}>
                  <Typography
                    variant="bodyPrimaryMed"
                    color="shades.neutral.800"
                  >
                    People with access
                  </Typography>
                </Row>
                <ShareTopicReportAccessList
                  entities={impersonations}
                  onRemove={handleRemoveAccess}
                  isError={isError}
                  isLoading={isLoading}
                />
              </Column>
            </AccessListWrapper>
          </AccessListCollapse>
        </ContentContainer>
        <Footer>
          <Row variant="right">
            {isAddingEntities ? (
              <SharingButton
                loading={createImpersonations.isPending}
                onClick={handleShare}
              >
                Share
              </SharingButton>
            ) : (
              <SharingButton onClick={onClose}>Done</SharingButton>
            )}
          </Row>
        </Footer>
      </Container>
    </Dialog>
  );
};

export default ShareTopicReportDialog;
